import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Avatar,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
  Container,
  Badge,
  InputBase,
  Paper,
  Collapse,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Notifications,
  AccountCircle,
  Dashboard,
  Article,
  ExitToApp,
  Search,
  Bookmark,
  ExpandLess,
  ExpandMore,
  KeyboardArrowDown,
} from "@mui/icons-material";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
// Removed hardcoded data import - now using API

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

// Simple ticker component using CSS animation for better compatibility
const TickerRow = ({ items, speed, type }) => {
  if (!items || items.length === 0) return null;

  // Create enough copies to ensure no gaps
  const copies = [...items, ...items, ...items, ...items];

  return (
    <Box
      sx={{
        width: "100%",
        overflow: "hidden",
        py: 1,
        position: "relative",
      }}
    >
      <Box
        sx={{
          display: "inline-flex",
          whiteSpace: "nowrap",
          animation: `ticker-${type} ${speed}s linear infinite`,
          "&:hover": {
            animationPlayState: "paused",
          },
          "@keyframes ticker-commodity": {
            "0%": { transform: "translateX(0)" },
            "100%": { transform: "translateX(-50%)" },
          },
          "@keyframes ticker-currency": {
            "0%": { transform: "translateX(0)" },
            "100%": { transform: "translateX(-50%)" },
          },
          "@keyframes ticker-exchange": {
            "0%": { transform: "translateX(0)" },
            "100%": { transform: "translateX(-50%)" },
          },
          "@keyframes ticker-stock": {
            "0%": { transform: "translateX(0)" },
            "100%": { transform: "translateX(-50%)" },
          },
        }}
      >
        {copies.map((item, index) => (
          <Box key={`${type}-${index}`} sx={{ mx: 0.5 }}>
            <TickerItem item={item} type={type} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// Ticker item component
const TickerItem = ({ item, type }) => {
  // Format price with appropriate decimals
  const formatPrice = (price) => {
    if (price === null || price === undefined || isNaN(Number(price)))
      return "N/A";

    const numPrice = Number(price);
    if (numPrice >= 1000) {
      return numPrice.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    } else if (numPrice >= 100) {
      return numPrice.toFixed(1);
    } else {
      return numPrice.toFixed(2);
    }
  };

  // Format change percentage - with robust type checking
  const formatChangePercent = (percent) => {
    // Handle null, undefined, NaN, or non-numeric values
    if (percent === null || percent === undefined || isNaN(Number(percent))) {
      return "—"; // Use dash for missing data
    }

    // Ensure percent is a number
    const numPercent = Number(percent);
    return numPercent > 0
      ? `+${numPercent.toFixed(2)}%`
      : numPercent < 0
      ? `${numPercent.toFixed(2)}%`
      : `0.00%`; // Show 0.00% for zero change
  };

  // Ensure item.change is a number (defaults to 0 if not provided)
  const change = item.change !== undefined && item.change !== null ? Number(item.change) : 0;
  const isPositive = change >= 0;
  const color = isPositive ? "#2e7d32" : "#d32f2f"; // Dark green for positive, dark red for negative
  const showChangeIndicator = true; // Always show indicators for all types

  return (
    <Paper
      elevation={0}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        p: 1,
        borderRadius: 1,
        border: "1px solid #e0e0e0",
        bgcolor: "#ffffff",
        minWidth: type === "stock" ? "120px" : "150px",
        height: "60px",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {showChangeIndicator && (
          isPositive ? (
            <ArrowDropUpIcon sx={{ color: "#2e7d32", fontSize: 24 }} />
          ) : (
            <ArrowDropDownIcon sx={{ color: "#d32f2f", fontSize: 24 }} />
          )
        )}
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: "normal", fontSize: 18, whiteSpace: "nowrap" }}
          >
            {type === "exchange" && item.name
              ? item.name.split(" ")[0]
              : type === "stock"
              ? item.symbol
              : type === "currency"
              ? item.symbol || item.name
              : item.name || item.symbol}
          </Typography>
          <Typography variant="body2">{formatPrice(item.price)}</Typography>
        </Box>
      </Box>
      <Box sx={{ ml: 2, textAlign: "right" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          {/* Show change percentage for all types including currencies */}
          <Typography
            variant="body2"
            sx={{
              color: color,
              fontWeight: "medium",
              mr: 0.5,
            }}
          >
            {formatChangePercent(change)}
          </Typography>
          {showChangeIndicator && (
            isPositive ? (
              <ThumbUpIcon sx={{ color: color, fontSize: 16 }} />
            ) : (
              <ThumbDownIcon sx={{ color: color, fontSize: 16 }} />
            )
          )}
        </Box>
      </Box>
    </Paper>
  );
};

const Header = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Fix for conditional hook call - always call useAuth unconditionally
  let currentUser = null;
  let logout = () => {};
  try {
    // If useAuth is available, use it; otherwise use default values
    const auth = useAuth();
    currentUser = auth?.currentUser || null;
    logout = auth?.logout || (() => {});
  } catch (error) {
    console.warn("Auth context not available:", error);
    // Continue with default values
  }

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [marketData, setMarketData] = useState({
    commodities: [],
    currencies: [],
    exchanges: [],
    stocks: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Hierarchical menu state
  const [menuData, setMenuData] = useState([]);
  const [menuLoading, setMenuLoading] = useState(true);
  const [menuError, setMenuError] = useState(null);
  const [menuAnchorEls, setMenuAnchorEls] = useState({});
  const [openSubmenus, setOpenSubmenus] = useState({});

  // CSV parsing functions
  const parseCommoditiesData = (csvData) => {
    if (!csvData || !csvData.trim()) return [];
    const lines = csvData.trim().split("\n").filter(line => line.trim());
    return lines.map((line) => {
      const [name, symbol, price, change, date] = line.split(",");
      return {
        name: name?.trim() || '',
        symbol: symbol?.trim() || '',
        price: parseFloat(price) || 0,
        change: parseFloat(change) || 0,
        date: date?.trim() || '',
      };
    }).filter(item => (item.name || item.symbol) && !isNaN(item.price));
  };

  const parseCurrenciesData = (csvData) => {
    if (!csvData || !csvData.trim()) return [];
    const lines = csvData.trim().split("\n").filter(line => line.trim());
    
    // Skip header row if it exists (starts with "name" or "symbol")
    const dataLines = lines.filter(line => {
      const firstWord = line.split(",")[0]?.toLowerCase().trim();
      return firstWord !== "name" && firstWord !== "symbol";
    });
    
    return dataLines.map((line) => {
      const parts = line.split(",");
      
      // Primary format: Symbol,Price,Change,Date (4 columns) - with change data
      if (parts.length >= 4) {
        const symbol = parts[0]?.trim() || '';
        const price = parts[1]?.trim() || '';
        const change = parts[2]?.trim() || '';
        const date = parts[3]?.trim() || '';
        
        const parsedPrice = parseFloat(price);
        const parsedChange = parseFloat(change);
        
        if (isNaN(parsedPrice) || !symbol) {
          return null;
        }
        
        return {
          symbol: symbol,
          name: symbol,
          price: parsedPrice,
          change: isNaN(parsedChange) ? 0 : parsedChange,
          date: date || '',
        };
      }
      // Fallback: Symbol,Price,Date (3 columns) - defaults change to 0
      else if (parts.length >= 3) {
        const symbol = parts[0]?.trim() || '';
        const price = parts[1]?.trim() || '';
        const date = parts[2]?.trim() || '';
        
        const parsedPrice = parseFloat(price);
        
        if (isNaN(parsedPrice) || !symbol) {
          return null;
        }
        
        return {
          symbol: symbol,
          name: symbol,
          price: parsedPrice,
          change: 0, // Default to 0 for display (will show 0.00%)
          date: date || '',
        };
      } else {
        // Unknown format, skip
        return null;
      }
    }).filter(item => item && item.symbol && !isNaN(item.price));
  };

  const parseExchangesData = (csvData) => {
    if (!csvData || !csvData.trim()) return [];
    const lines = csvData.trim().split("\n").filter(line => line.trim());
    return lines.map((line) => {
      const [name, symbol, price, change, date] = line.split(",");
      return {
        name: name?.trim() || '',
        symbol: symbol?.trim() || '',
        price: parseFloat(price) || 0,
        change: parseFloat(change) || 0,
        date: date?.trim() || '',
      };
    }).filter(item => (item.name || item.symbol) && !isNaN(item.price));
  };

  const parseStocksData = (csvData) => {
    if (!csvData || !csvData.trim()) return [];
    const lines = csvData.trim().split("\n").filter(line => line.trim());
    return lines.map((line) => {
      const [name, symbol, price, change, date] = line.split(",");
      return {
        name: name?.trim() || '',
        symbol: symbol?.trim() || '',
        price: parseFloat(price) || 0,
        change: parseFloat(change) || 0,
        date: date?.trim() || '',
      };
    }).filter(item => (item.name || item.symbol) && !isNaN(item.price));
  };

  // Load CSV data from API on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Helper function to fetch and parse CSV
        const fetchCSV = async (url) => {
          const response = await fetch(url);
          if (!response.ok) {
            console.error(`Failed to fetch ${url}: ${response.status} ${response.statusText}`);
            return '';
          }
          const text = await response.text();
          // Debug: Log first few lines of CSV for currencies
          if (url.includes('fx.csv')) {
            console.log('FX CSV response (first 3 lines):', text.split('\n').slice(0, 3));
          }
          return text;
        };

        // Fetch CSV data from API endpoint
        const [commoditiesResponse, currenciesResponse, exchangesResponse, stocksResponse] =
          await Promise.all([
            fetchCSV(`${apiUrl}/csv/commodities.csv`),
            fetchCSV(`${apiUrl}/csv/fx.csv`),
            fetchCSV(`${apiUrl}/csv/indices.csv`),
            fetchCSV(`${apiUrl}/csv/sp500_most_active.csv`).catch(() => ''),
          ]);

        // Parse CSV data
        const commodities = parseCommoditiesData(commoditiesResponse);
        const currencies = parseCurrenciesData(currenciesResponse);
        const exchanges = parseExchangesData(exchangesResponse);
        const stocks = stocksResponse ? parseStocksData(stocksResponse) : [];

        setMarketData({ commodities, currencies, exchanges, stocks });
        setError(null);
        
        console.log("Header ticker data loaded:", {
          commodities: commodities.length,
          currencies: currencies.length,
          exchanges: exchanges.length,
          stocks: stocks.length,
        });
        
        // Debug: Log first currency to verify parsing
        if (currencies.length > 0) {
          console.log("First currency parsed:", currencies[0]);
          console.log("Total currencies loaded:", currencies.length);
        } else {
          console.warn("No currencies loaded. CSV response:", currenciesResponse.substring(0, 200));
        }
      } catch (err) {
        console.error("Error loading market data:", err);
        setError("Failed to load market data");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Fetch hierarchical menu data
  useEffect(() => {
    fetchMenuData();
  }, []);

  const fetchMenuData = async () => {
    try {
      setMenuLoading(true);
      setMenuError(null);
      const response = await axios.get(`${apiUrl}/menus`);
      setMenuData(response.data);
    } catch (err) {
      console.error("Error fetching menu data:", err);
      setMenuError(err.message || "Failed to fetch menu data");
      // Fallback to default menu items if API fails
      setMenuData([
        { id: 1, name: "Home", url: "/", children: [] },
        { id: 2, name: "Articles", url: "/articles", children: [] },
        { id: 3, name: "About", url: "/about", children: [] },
        { id: 4, name: "Contact", url: "/contact", children: [] },
      ]);
    } finally {
      setMenuLoading(false);
    }
  };

  // Apply menu item styling from database
  const getMenuItemStyle = (menuItem) => ({
    fontFamily: menuItem.font_family || "inherit",
    fontSize: menuItem.font_size || "inherit",
    fontWeight: menuItem.font_weight || "inherit",
    color: menuItem.color || "inherit",
    fontStyle: menuItem.style || "inherit",
    textDecoration: menuItem.style === "underline" ? "underline" : "none",
  });

  // Handle desktop dropdown menus
  const handleMenuOpen = (event, menuId) => {
    setMenuAnchorEls((prev) => ({
      ...prev,
      [menuId]: event.currentTarget,
    }));
  };

  const handleMenuClose = (menuId) => {
    setMenuAnchorEls((prev) => ({
      ...prev,
      [menuId]: null,
    }));
  };

  // Handle mobile submenu toggle
  const handleSubmenuToggle = (menuId) => {
    setOpenSubmenus((prev) => ({
      ...prev,
      [menuId]: !prev[menuId],
    }));
  };

  // Render desktop menu items recursively
  const renderDesktopMenuItem = (menuItem) => {
    const hasChildren = menuItem.children && menuItem.children.length > 0;
    const anchorEl = menuAnchorEls[menuItem.id];
    const menuStyle = getMenuItemStyle(menuItem);

    if (hasChildren) {
      return (
        <Box key={menuItem.id} sx={{ display: "inline-block" }}>
          <Button
            sx={{
              color: "inherit",
              display: "flex",
              alignItems: "center",
              textTransform: "none",
              ...menuStyle,
            }}
            onClick={(e) => handleMenuOpen(e, menuItem.id)}
            endIcon={<KeyboardArrowDown />}
          >
            {menuItem.name}
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => handleMenuClose(menuItem.id)}
            PaperProps={{
              sx: {
                minWidth: 200,
                maxWidth: 300,
              },
            }}
          >
            {menuItem.children.map((child) => (
              <div key={child.id}>
                {child.children && child.children.length > 0 ? (
                  // Nested submenu item
                  <MenuItem
                    sx={{
                      ...getMenuItemStyle(child),
                      justifyContent: "space-between",
                    }}
                    onClick={() => {
                      // Handle nested submenu navigation
                      if (child.url) {
                        window.location.href = child.url;
                      }
                      handleMenuClose(menuItem.id);
                    }}
                  >
                    <span>{child.name}</span>
                    <KeyboardArrowDown />
                  </MenuItem>
                ) : (
                  // Regular menu item
                  <MenuItem
                    component={child.url ? Link : "div"}
                    to={child.url || undefined}
                    onClick={() => handleMenuClose(menuItem.id)}
                    sx={getMenuItemStyle(child)}
                  >
                    {child.name}
                  </MenuItem>
                )}
                {/* Render third-level items if they exist */}
                {child.children &&
                  child.children.map((grandChild) => (
                    <MenuItem
                      key={grandChild.id}
                      component={grandChild.url ? Link : "div"}
                      to={grandChild.url || undefined}
                      onClick={() => handleMenuClose(menuItem.id)}
                      sx={{
                        ...getMenuItemStyle(grandChild),
                        pl: 4, // Indent for third level
                      }}
                    >
                      {grandChild.name}
                    </MenuItem>
                  ))}
              </div>
            ))}
          </Menu>
        </Box>
      );
    }

    // Regular menu item without children
    return (
      <Button
        key={menuItem.id}
        component={menuItem.url ? Link : "div"}
        to={menuItem.url || undefined}
        sx={{
          color: "inherit",
          display: "block",
          textTransform: "none",
          ...menuStyle,
        }}
      >
        {menuItem.name}
      </Button>
    );
  };

  // Render mobile menu items recursively
  const renderMobileMenuItem = (menuItem, level = 0) => {
    const hasChildren = menuItem.children && menuItem.children.length > 0;
    const isOpen = openSubmenus[menuItem.id];
    const menuStyle = getMenuItemStyle(menuItem);

    return (
      <div key={menuItem.id}>
        <ListItem
          button
          component={hasChildren ? "div" : menuItem.url ? Link : "div"}
          to={!hasChildren && menuItem.url ? menuItem.url : undefined}
          onClick={
            hasChildren
              ? () => handleSubmenuToggle(menuItem.id)
              : handleDrawerToggle
          }
          sx={{
            pl: 2 + level * 2,
            ...menuStyle,
          }}
        >
          <ListItemText
            primary={menuItem.name}
            sx={{
              "& .MuiTypography-root": menuStyle,
            }}
          />
          {hasChildren && (isOpen ? <ExpandLess /> : <ExpandMore />)}
        </ListItem>
        {hasChildren && (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {menuItem.children.map((child) =>
                renderMobileMenuItem(child, level + 1)
              )}
            </List>
          </Collapse>
        )}
      </div>
    );
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuCloseProfile = () => {
    setAnchorEl(null);
  };

  const handleNotificationMenuOpen = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleLogout = () => {
    logout();
    handleMenuCloseProfile();
  };

  const renderMobileMenu = (
    <Drawer
      anchor="left"
      open={drawerOpen}
      onClose={handleDrawerToggle}
      sx={{
        "& .MuiDrawer-paper": { width: 240, boxSizing: "border-box" },
      }}
    >
      <Box
        sx={{
          p: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {currentUser ? (
          <>
            <Avatar
              sx={{ width: 60, height: 60, mb: 1, bgcolor: "primary.main" }}
            >
              {currentUser.firstName?.charAt(0) ||
                currentUser.email?.charAt(0) ||
                "U"}
            </Avatar>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
              {currentUser.firstName
                ? `${currentUser.firstName} ${currentUser.lastName}`
                : currentUser.email}
            </Typography>
          </>
        ) : null}
      </Box>
      <Divider />

      {/* Dynamic Hierarchical Menu for Mobile */}
      <List>
        {menuLoading ? (
          <ListItem>
            <ListItemText primary="Loading menu..." />
          </ListItem>
        ) : menuError ? (
          <ListItem>
            <ListItemText primary="Menu unavailable" />
          </ListItem>
        ) : (
          menuData.map((menuItem) => renderMobileMenuItem(menuItem))
        )}
      </List>

      {/* Additional static menu items */}
      <Divider />
      <List>
        <ListItem
          button
          component={Link}
          to="/bookmarks"
          onClick={handleDrawerToggle}
        >
          <ListItemIcon>
            <Bookmark />
          </ListItemIcon>
          <ListItemText primary="Bookmarks" />
        </ListItem>
      </List>

      <Divider />
      <List>
        {currentUser ? (
          <>
            <ListItem
              button
              component={Link}
              to="/profile"
              onClick={handleDrawerToggle}
            >
              <ListItemIcon>
                <AccountCircle />
              </ListItemIcon>
              <ListItemText primary="Profile" />
            </ListItem>
            <ListItem
              button
              onClick={() => {
                handleLogout();
                handleDrawerToggle();
              }}
            >
              <ListItemIcon>
                <ExitToApp />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItem>
          </>
        ) : (
          <>
            <ListItem
              button
              component={Link}
              to="/sign-in"
              onClick={handleDrawerToggle}
            >
              <ListItemIcon>
                <AccountCircle />
              </ListItemIcon>
              <ListItemText primary="Sign In" />
            </ListItem>
            <ListItem
              button
              component={Link}
              to="/sign-up"
              onClick={handleDrawerToggle}
            >
              <ListItemIcon>
                <AccountCircle />
              </ListItemIcon>
              <ListItemText primary="Sign Up" />
            </ListItem>
          </>
        )}
      </List>
    </Drawer>
  );

  return (
    <>
      <AppBar position="static" color="default" elevation={1}>
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ px: 0 }}>
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            <Typography
              variant="h6"
              noWrap
              component={Link}
              to="/"
              sx={{
                mr: 2,
                display: { xs: "flex" },
                fontWeight: 700,
                color: "inherit",
                textDecoration: "none",
              }}
            >
            </Typography>

            {/* Dynamic Hierarchical Menu for Desktop */}
            {!isMobile && (
              <Box sx={{ display: "flex" }}>
                {menuLoading ? (
                  <Typography variant="body2" sx={{ color: "inherit", p: 1 }}>
                    Loading...
                  </Typography>
                ) : menuError ? (
                  // Fallback to default menu if API fails
                  <>
                    <Button
                      component={Link}
                      to="/"
                      sx={{ color: "inherit", display: "block" }}
                    >
                      Home
                    </Button>
                    <Button
                      component={Link}
                      to="/articles"
                      sx={{ color: "inherit", display: "block" }}
                    >
                      Articles
                    </Button>
                    <Button
                      component={Link}
                      to="/about"
                      sx={{ color: "inherit", display: "block" }}
                    >
                      About
                    </Button>
                    <Button
                      component={Link}
                      to="/contact"
                      sx={{ color: "inherit", display: "block" }}
                    >
                      Contact
                    </Button>
                  </>
                ) : (
                  menuData.map((menuItem) => renderDesktopMenuItem(menuItem))
                )}
              </Box>
            )}

            <Box sx={{ flexGrow: 1 }} />

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Paper
                component="form"
                sx={{
                  p: "2px 4px",
                  display: "flex",
                  alignItems: "center",
                  width: 200,
                  mr: 2,
                  border: "1px solid #e0e0e0",
                  boxShadow: "none",
                }}
              >
                <InputBase
                  sx={{ ml: 1, flex: 1 }}
                  placeholder="Search"
                  inputProps={{ "aria-label": "search" }}
                />
                <IconButton
                  type="submit"
                  sx={{ p: "10px" }}
                  aria-label="search"
                >
                  <Search />
                </IconButton>
              </Paper>

              {currentUser && (
                <>
                  <IconButton
                    color="inherit"
                    aria-label="show notifications"
                    onClick={handleNotificationMenuOpen}
                    sx={{ mr: 1 }}
                  >
                    <Badge badgeContent={3} color="error">
                      <Notifications />
                    </Badge>
                  </IconButton>
                  <IconButton
                    edge="end"
                    aria-label="account of current user"
                    aria-haspopup="true"
                    onClick={handleProfileMenuOpen}
                    color="inherit"
                  >
                    <Avatar
                      sx={{ width: 32, height: 32, bgcolor: "primary.main" }}
                    >
                      {currentUser.firstName?.charAt(0) ||
                        currentUser.email?.charAt(0) ||
                        "U"}
                    </Avatar>
                  </IconButton>
                </>
              )}

              {!currentUser && !isMobile && (
                <>
                  <Button component={Link} to="/sign-in" color="inherit">
                    Sign In
                  </Button>
                  <Button
                    component={Link}
                    to="/sign-up"
                    variant="contained"
                    color="primary"
                    sx={{ ml: 1 }}
                  >
                    Sign Up
                  </Button>
                </>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Market Tickers - CSS-based scrolling for better compatibility */}
      <Box
        sx={{
          width: "100%",
          bgcolor: "#f5f5f5",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        {/* Commodities - Slowest scrolling */}
        <TickerRow items={marketData.commodities} speed={60} type="commodity" />

        {/* Currencies - Medium scrolling */}
        <TickerRow items={marketData.currencies} speed={45} type="currency" />

        {/* Exchanges - Medium-fast scrolling */}
        <TickerRow items={marketData.exchanges} speed={40} type="exchange" />

        {/* Stocks - Fastest scrolling */}
        <TickerRow items={marketData.stocks} speed={30} type="stock" />
      </Box>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={Boolean(anchorEl)}
        onClose={handleMenuCloseProfile}
      >
        <MenuItem
          component={Link}
          to="/profile"
          onClick={handleMenuCloseProfile}
        >
          Profile
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>Logout</MenuItem>
      </Menu>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationAnchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={Boolean(notificationAnchorEl)}
        onClose={handleNotificationMenuClose}
      >
        <MenuItem onClick={handleNotificationMenuClose}>
          <Typography variant="body2">
            New article: "Market Trends 2025"
          </Typography>
        </MenuItem>
        <MenuItem onClick={handleNotificationMenuClose}>
          <Typography variant="body2">Your comment received a reply</Typography>
        </MenuItem>
        <MenuItem onClick={handleNotificationMenuClose}>
          <Typography variant="body2">
            System update: New features available
          </Typography>
        </MenuItem>
      </Menu>

      {renderMobileMenu}
    </>
  );
};

export default Header;
